<template>
  <section v-if="showPrices" class="tps-summary">
    <div class="tps-summary-body">
      <h4 class="tps-summary-title">Plan Summary</h4>

      <div class="tps-summary-row">
        <span class="tps-summary-label">Total Fee</span>
        <span class="tps-summary-amt">{{ formatFee(displayTotalFee) }}</span>
      </div>

      <template v-if="totalInsuranceSecondary > 0">
        <div class="tps-summary-row">
          <span class="tps-summary-label">Estimated Primary Insurance</span>
          <span class="tps-summary-amt tps-summary-credit">−{{ formatFee(totalInsurancePrimary) }}</span>
        </div>
        <div class="tps-summary-row">
          <span class="tps-summary-label">Estimated Secondary Insurance</span>
          <span class="tps-summary-amt tps-summary-credit">−{{ formatFee(totalInsuranceSecondary) }}</span>
        </div>
      </template>
      <div v-else-if="totalInsurance > 0" class="tps-summary-row">
        <span class="tps-summary-label">Estimated Insurance Coverage</span>
        <span class="tps-summary-amt tps-summary-credit">−{{ formatFee(totalInsurance) }}</span>
      </div>

      <!-- The PPO/network reduction — Open Dental's "Discount" column. Distinct
           from the Practice Discount below (procedurelog.Discount); both can
           apply to the same plan. Without this row "Total Fee" was the
           POST-write-off net and did not match OD's Fee subtotal. -->
      <div v-if="totalWriteOff > 0.01" class="tps-summary-row">
        <span class="tps-summary-label">Insurance Discount</span>
        <span class="tps-summary-amt tps-summary-credit">−{{ formatFee(totalWriteOff) }}</span>
      </div>

      <div v-if="totalDiscount > 0 && showDiscount" class="tps-summary-row">
        <span class="tps-summary-label">Practice Discount</span>
        <span class="tps-summary-amt tps-summary-credit">−{{ formatFee(totalDiscount) }}</span>
      </div>

      <!-- Guarantor account balance / credit from Open Dental. Positive = owed
           (adds to the portion), negative = credit (−$x, subtracts). Folded into
           the green Estimated Patient Portion bar below via totalPatient. -->
      <div v-if="showBalanceRow" class="tps-summary-row">
        <span class="tps-summary-label">Remaining Balance / Credits</span>
        <span class="tps-summary-amt" :class="{ 'tps-summary-credit': balanceAmount < 0 }">
          <template v-if="balanceAmount < 0">−{{ formatFee(Math.abs(balanceAmount)) }}</template>
          <template v-else>{{ formatFee(balanceAmount) }}</template>
        </span>
      </div>
    </div>

    <!-- Green bar = Estimated Patient Portion (before any per-option fee).
         When a chosen option carries an Additional Fee, render the fee row +
         a separate TOTAL DUE row directly below; the chosen-option
         breakdown then computes off the new total. -->
    <div class="tps-summary-final">
      <span class="tps-summary-final-label">{{ patientPortionLabel }}</span>
      <span class="tps-summary-final-amt">{{ formatFee(totalPatient) }}</span>
    </div>

    <div v-if="breakdown && breakdown.fee > 0" class="tps-summary-fee">
      <div class="tps-summary-fee-row">
        <span class="tps-fee-label">{{ breakdown.feeLabel || 'Additional fee' }}</span>
        <span class="tps-fee-amt">+{{ formatFee(breakdown.fee) }}</span>
      </div>
      <div class="tps-summary-fee-row tps-summary-fee-total">
        <span class="tps-fee-total-label">Total Due</span>
        <span class="tps-fee-total-amt">{{ formatFee(breakdown.totalDue) }}</span>
      </div>
    </div>

    <!-- Chosen payment option breakdown — appears once the TC/patient picks an
         option. Reflects the option's terms against the (fee-inclusive) total,
         minus any chairside Pay today. -->
    <div v-if="breakdown" class="tps-summary-chosen">
      <div class="tps-chosen-head">
        <i class="fa fa-check-circle"></i>
        <span class="tps-chosen-title">{{ selectedOption.title }}</span>
      </div>

      <!-- Pay today (chairside downpayment) — shown for split/finance options
           when the TC entered a value. Visually sits at the top of the
           breakdown so it reads as "today's money, then the rest." -->
      <div v-if="breakdown.downpayment > 0" class="tps-chosen-row tps-chosen-paytoday">
        <span>Pay today</span>
        <span class="tps-chosen-strong">{{ formatFee(breakdown.downpayment) }}</span>
      </div>

      <!-- Pay-in-full discount (applied to the fee-inclusive total). -->
      <template v-if="breakdown.kind === 'discount'">
        <!-- Only show the discount line when there's an actual discount — a 0%
             pay-in-full would otherwise render a confusing "0% … −$0" row. -->
        <div v-if="breakdown.discountAmt > 0" class="tps-chosen-row">
          <span>{{ breakdown.discountPct }}% paid-in-full discount</span>
          <span class="tps-summary-credit">−{{ formatFee(breakdown.discountAmt) }}</span>
        </div>
        <div class="tps-chosen-row tps-chosen-strong">
          <span>You pay</span>
          <span>{{ formatFee(breakdown.finalAmount) }}</span>
        </div>
      </template>

      <!-- Split / in-house equal payments — perPayment is computed off the
           remaining balance after Pay today + fee, so the numbers read
           chairside-correct when either is in play. -->
      <template v-else-if="breakdown.kind === 'split'">
        <div class="tps-chosen-row tps-chosen-strong">
          <span>{{ remainingLabel(breakdown) }}</span>
          <span>{{ breakdown.count }} × {{ formatFee(breakdown.perPayment) }}</span>
        </div>
        <!-- Optional dated schedule when due dates are filled in -->
        <div v-if="hasPaymentDates" class="tps-chosen-schedule">
          <div v-for="n in breakdown.count" :key="n" class="tps-chosen-row tps-chosen-sched-row">
            <span>Payment {{ n }}<template v-if="formatDate(paymentDates[n - 1])"> · {{ formatDate(paymentDates[n - 1]) }}</template></span>
            <span>{{ formatFee(breakdown.perPayment) }}</span>
          </div>
        </div>
      </template>

      <!-- Financing — informational tier menu (estimated monthly per term).
           No single "you pay": financing is a 3rd-party application, so we show
           the same tier rows the patient/PDF see. A Cherry-backed option shows
           Cherry's real plans (or no figures) instead — its breakdown has no typed
           tiers, so the rows below render nothing for it. -->
      <template v-else-if="breakdown.kind === 'finance'">
        <CherryTierList
          v-if="isCherryBacked(selectedOption)"
          variant="summary"
          :opt="selectedOption"
          :terms="cherryTerms"
          :amount="cherryAmount"
          :as-of-text="cherryAsOfText"
          :updating="cherryUpdating"
        />
        <div
          v-for="(t, ti) in (breakdown.tiers || [])"
          :key="ti"
          class="tps-chosen-row tps-chosen-sched-row"
        >
          <span>{{ tierLabel(t) }}</span>
          <span>~{{ formatFee(Math.round(t.monthly)) }}/mo</span>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { computePaymentBreakdown, financeTierLabel } from '../utils/paymentOptions.js';
import { insuranceSplit, writeOffOf } from '../utils/insuranceSplit.js';
import { patientPortion } from '../utils/patientPortion.js';
import { isCherryBacked, cherryAmountFor } from '../utils/cherryTerms.js';
import CherryTierList from './CherryTierList.vue';

export default {
  name: 'PlanFinancialSummary',
  components: { CherryTierList },
  props: {
    procedures: { type: Array, default: () => [] },
    showPrices: { type: Boolean, default: true },
    showDiscount: { type: Boolean, default: false },
    // When on, render the "Remaining Balance / Credits" row and fold the
    // guarantor's OD account balance into the Estimated Patient Portion.
    showBalance: { type: Boolean, default: false },
    // Signed guarantor balance = BalTotal − InsEst. Positive = owed (adds),
    // negative = credit (subtracts). Ignored unless showBalance is on.
    familyBalance: { type: [Number, String], default: 0 },
    // The chosen payment option (resolved object) — when set, its breakdown
    // renders under the patient portion. Builder passes none.
    selectedOption: { type: Object, default: null },
    // Optional per-payment due dates (ISO strings) for split/in-house options.
    paymentDates: { type: Array, default: () => [] },
    // Chairside "Pay today" downpayment for the chosen option ($, optional).
    // Subtracts from the (fee-inclusive) total before per-payment math.
    downpayment: { type: [Number, String], default: 0 },
    // Cherry's real plans for the chosen option ({ purchaseAmount, plans, fetchedAt } or
    // null). Only read when the chosen option is Cherry-backed; the typed path ignores it.
    cherryTerms: { type: Object, default: null },
    // Pre-formatted "as of" date (site timezone) for the Cherry disclosure line.
    cherryAsOfText: { type: String, default: '' },
    // True while Cherry's terms are being (re)fetched for a changed amount.
    cherryUpdating: { type: Boolean, default: false },
  },
  computed: {
    totalFee() {
      return this.procedures.reduce((s, p) => s + (parseFloat(p.fee) || 0), 0);
    },
    // The PPO write-off — OD's "Discount" column, the gap between UCR and the
    // negotiated fee. Kept separate from `totalDiscount` (the membership
    // reduction): both can apply at once and each gets its own row.
    totalWriteOff() {
      return this.procedures.reduce((s, p) => s + writeOffOf(p), 0);
    },
    // "Total Fee" adds back only the reductions rendered as their own rows
    // below it, so the card always reads down to the patient portion, OD-style:
    //
    //     Total Fee − [Insurance Discount] − [Practice Discount] − Insurance
    //
    // The practice discount is added back only when its layer is on (existing
    // behaviour); the write-off whenever there is one, since its row is not
    // layer-gated. Matches ItemizedView.vue subtotalTotalFee so the group
    // subtotals and this card cannot disagree — before the write-off row
    // existed this read the POST-write-off net and Serenity Grove's $2,569
    // Priority 3 showed as $1,177.
    displayTotalFee() {
      const discountAddBack = this.showDiscount && this.totalDiscount > 0 ? this.totalDiscount : 0;
      const writeOffAddBack = this.totalWriteOff > 0.01 ? this.totalWriteOff : 0;
      return this.totalFee + discountAddBack + writeOffAddBack;
    },
    // Guarantor account balance / credit, gated by the showBalance toggle.
    // 0 when the toggle is off. Positive = owed (adds to the portion),
    // negative = credit (subtracts).
    balanceAmount() {
      return this.showBalance ? (parseFloat(this.familyBalance) || 0) : 0;
    },
    // Hide the row for a negligible balance (incl. OD denormalized-zero, which
    // the backend already rounds to 0.00).
    showBalanceRow() {
      return Math.abs(this.balanceAmount) >= 0.01;
    },
    totalPatient() {
      return patientPortion(this.procedures, this.balanceAmount);
    },
    // Insurance coverage, split into primary/secondary via the shared helper
    // (explicit backend fields when present, else the combined fallback on
    // primary). `totalInsurance` stays the combined figure so the single-row
    // rendering + patientPortionLabel are unchanged for single-insurance plans.
    totalInsurancePrimary() {
      return this.procedures.reduce((s, p) => s + insuranceSplit(p).primary, 0);
    },
    totalInsuranceSecondary() {
      return this.procedures.reduce((s, p) => s + insuranceSplit(p).secondary, 0);
    },
    totalInsurance() {
      return this.totalInsurancePrimary + this.totalInsuranceSecondary;
    },
    totalDiscount() {
      return this.procedures.reduce((s, p) => s + (parseFloat(p.discount) || 0), 0);
    },
    patientPortionLabel() {
      return this.totalInsurance > 0 ? 'Estimated Patient Portion' : 'Patient Total';
    },
    breakdown() {
      if (!this.selectedOption) return null;
      const b = computePaymentBreakdown(
        this.selectedOption,
        this.totalPatient,
        { downpayment: this.downpayment },
      );
      return b && b.kind !== 'custom' ? b : null;
    },
    // What Cherry must have priced for its figures to show here: patient portion + the
    // option's fee − Pay today, i.e. this very breakdown's remainder.
    cherryAmount() {
      return this.breakdown ? cherryAmountFor(this.breakdown) : null;
    },
    hasPaymentDates() {
      return Array.isArray(this.paymentDates) && this.paymentDates.some((d) => d);
    },
  },
  methods: {
    // Header for the split row — reads "2 payments" by default, or "Remaining
    // (2 payments)" when a Pay today subtracted from the total. Reinforces
    // chairside what the per-payment math is computed against.
    remainingLabel(b) {
      const word = b.count + (b.count === 1 ? ' payment' : ' payments');
      return b.downpayment > 0 ? `Remaining (${word})` : word;
    },
    tierLabel(t) {
      return financeTierLabel(t);
    },
    /** Template access to the shared predicate — see utils/cherryTerms.js. */
    isCherryBacked(opt) {
      return isCherryBacked(opt);
    },
    formatFee(n) {
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(iso) {
      if (!iso) return '';
      // Native date inputs give 'YYYY-MM-DD'; parse as local to avoid TZ shift.
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
      const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
  },
};
</script>

<style scoped>
.tps-summary {
  margin: 36px 0 0 auto;
  max-width: 460px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  background: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.tps-summary-body {
  padding: 18px 22px 14px;
}

.tps-summary-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 11.5px;
  font-weight: 500;
  color: #0d5f54;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  margin: 0 0 12px 0;
}

.tps-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 0;
  font-size: 13px;
  color: #475569;
}
.tps-summary-row + .tps-summary-row {
  border-top: 1px dashed #f1f3f6;
}

.tps-summary-label {
  font-weight: 500;
}

.tps-summary-amt {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #1e293b;
}

.tps-summary-credit { color: #64748b; }

.tps-summary-final {
  padding: 16px 22px;
  background: linear-gradient(135deg, #0d5f54 0%, #14776a 100%);
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 14px;
}

.tps-summary-final-label {
  color: #d8eeeb;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.tps-summary-final-amt {
  color: #ffffff;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 22px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
}

/* Additional Fee block — sits below the green Patient Portion bar when the
   chosen option carries a fee. The first row is the fee itself; the second
   is the recomputed "Total Due" the chosen-option math runs against. */
.tps-summary-fee {
  padding: 12px 22px 14px;
  background: #fffbeb;       /* warm amber tint to read as "additional charge" */
  border-top: 1px solid #fef3c7;
}
.tps-summary-fee-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 14px;
  font-size: 13px;
  color: #78350f;
  padding: 3px 0;
}
.tps-fee-label { font-weight: 500; }
.tps-fee-amt {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #78350f;
}
.tps-summary-fee-total {
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px dashed #fcd34d;
}
.tps-fee-total-label {
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 11px;
  font-weight: 700;
  color: #92400e;
}
.tps-fee-total-amt {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 17px;
  font-weight: 600;
  color: #78350f;
  font-variant-numeric: tabular-nums;
}

/* Pay today (chairside downpayment) row — appears at the top of the
   chosen-option breakdown when the TC entered an amount. */
.tps-chosen-paytoday {
  border-bottom: 1px dashed #e4e7ec;
  padding-bottom: 6px;
  margin-bottom: 6px;
  color: #0d5f54;
}
.tps-chosen-paytoday > span:first-child {
  font-weight: 600;
}

/* Chosen payment option breakdown */
.tps-summary-chosen {
  padding: 14px 22px 16px;
  border-top: 1px solid #e4e7ec;
  background: #f8fafb;
}
.tps-chosen-head {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
}
.tps-chosen-head .fa { color: #0d9488; font-size: 13px; }
.tps-chosen-title {
  font-weight: 600;
  font-size: 13px;
  color: #0d5f54;
}
.tps-chosen-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
  color: #475569;
  padding: 3px 0;
}
.tps-chosen-strong {
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}
.tps-chosen-schedule {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e4e7ec;
}
.tps-chosen-sched-row {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  padding: 2px 0;
}

/* Narrow surfaces (Patient mobile, Present Mode portrait) — let the
   summary span the full width instead of right-aligning. */
@media (max-width: 640px) {
  .tps-summary {
    margin: 24px 0 0 0;
    max-width: 100%;
  }
  .tps-summary-final-amt { font-size: 18px; }
}

/* Print — keep the whole summary card together on one page. */
@media print {
  .tps-summary { break-inside: avoid; }
}
</style>
